import React, { useState, useEffect, useMemo } from "react";
import "./index.m.css";
import { SuccessIcon } from "./icons";
import { SUBSCRIBE_PAGE_TEXT } from "../../../../constants";

const REDIRECT_URL_QUERY_PARAM = "redirect_url";
const PREMIUM_LISTING_URL = "/premium";
const EPAPER_ORIGIN = "https://epaper.prajavani.net";

const isHttpUrl = (value) => /^https?:\/\//i.test(value || "");

const getValidatedRedirectTarget = (value) => {
  if (!value || typeof value !== "string") return "";

  if (value.startsWith("/") && !value.startsWith("/subscribe")) {
    return value;
  }

  if (!isHttpUrl(value)) return "";

  try {
    const url = new URL(value);
    if (typeof window !== "undefined" && url.origin === window.location.origin && !url.pathname.startsWith("/subscribe")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    if (url.origin === EPAPER_ORIGIN || url.origin === "https://epaper-dev.prajavani.net") {
      return url.toString();
    }
  } catch (e) {
    return "";
  }

  return "";
};

const resolveRedirectUrl = () => {
  if (typeof window === "undefined") return PREMIUM_LISTING_URL;

  const params = new URLSearchParams(window.location.search);
  const redirectFromQuery = params.get(REDIRECT_URL_QUERY_PARAM) || "";
  const validatedTarget = getValidatedRedirectTarget(redirectFromQuery);
  if (validatedTarget) return validatedTarget;

  return PREMIUM_LISTING_URL;
};

export const SuccessPage = () => {
  const [countdown, setCountdown] = useState(5);
  const redirectUrl = useMemo(() => resolveRedirectUrl(), []);

  const redirectToTarget = () => {
    // Force a fresh document request when leaving success page.
    const target = new URL(redirectUrl, window.location.origin);
    console.log("Redirecting to:", target.toString());
    window.location.replace(target.toString());
  };

  const handleRedirectClick = (e) => {
    e.preventDefault();
    redirectToTarget();
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          redirectToTarget();
          return 1;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [redirectUrl]);
  const confettiData = useMemo(() => {
    return [...Array(50)].map((_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 3}s`,
      animationDuration: `${Math.random() * 2 + 3}s`,
      backgroundColor: [
        "#22c55e", // green-500
        "#16a34a", // green-600
        "#15803d", // green-700
        "#4ade80", // green-400
        "#86efac", // green-300
        "#fde047", // yellow-300
        "#facc15", // yellow-400
        "#eab308", // yellow-500
      ][Math.floor(Math.random() * 8)],
      width: `${Math.random() * 10 + 5}px`,
      height: `${Math.random() * 5 + 2}px`,
      transform: `rotate(${Math.random() * 360}deg)`,
    }));
  }, []);

  const Confetti = useMemo(
    () => (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, overflow: "hidden", pointerEvents: "none", width: "100vw", zIndex: 9999 }}>
        {confettiData.map((confetti) => (
          <div
            key={confetti.id}
            className="animate-confetti"
            style={{
              left: confetti.left,
              top: "-10px",
              animationDelay: confetti.animationDelay,
              animationDuration: confetti.animationDuration,
              backgroundColor: confetti.backgroundColor,
              width: confetti.width,
              height: confetti.height,
              transform: confetti.transform,
            }}
          />
        ))}
      </div>
    ),
    [confettiData],
  );

  return (
    <div styleName="successPageContainer">
      {Confetti}
      <div styleName="contentWrapper">
        <SuccessIcon styleName="successIcon" />
        <h1 styleName="title">{SUBSCRIBE_PAGE_TEXT.SUCCESS_TITLE}</h1>
        <p styleName="subtitle">{SUBSCRIBE_PAGE_TEXT.SUCCESS_SUBTITLE}</p>
        <a href={redirectUrl} onClick={handleRedirectClick}>
          <p styleName="redirectText">
            {countdown}{SUBSCRIBE_PAGE_TEXT.REDIRECT_PREFIX}<span styleName="redirectLink">{SUBSCRIBE_PAGE_TEXT.REDIRECT_LINK_TEXT}</span>{SUBSCRIBE_PAGE_TEXT.REDIRECT_SUFFIX}
          </p>
        </a>
      </div>
      <style>
        {`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotateZ(0deg);
            opacity: 1;
          }
          75% {
            transform: translateY(75vh) rotateZ(270deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotateZ(360deg);
            opacity: 0;
          }
        }

        .animate-confetti {
          position: absolute;
          animation: confetti linear infinite;
        }
      `}</style>
    </div>
  );
};
